import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Select,
  InputNumber,
  DatePicker,
  Checkbox,
  Button,
  Row,
  Col,
  Typography,
  Divider,
  Alert,
  Space
} from 'antd';
import {
  PlusOutlined,
  SaveOutlined
} from '@ant-design/icons';
import { createTask, updateTask } from '../../services/taskApi';
import dayjs from 'dayjs';

const { TextArea } = Input;
const { Option } = Select;
const { Title, Text } = Typography;

const TaskForm = ({
  projectId,
  task = null,
  onSave,
  onCancel,
  users = [],
  isEditing = false
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [validationErrors, setValidationErrors] = useState({});

  // ตั้งค่าเริ่มต้นเมื่อมี task (สำหรับแก้ไข)
  useEffect(() => {

    console.log('task', task);

    if (isEditing && task) {
      const initialValues = {
        task_title: task.task_title || '',
        task_description: task.task_description || '',
        status: task.status || 'todo',
        priority: task.priority || 'medium',
        assigned_to: task.assigned_to || '',
        progress_percentage: task.progress_percentage || 0,
        start_date: dayjs(task.start_date),
        due_date: dayjs(task.due_date),
        estimated_hours: task.estimated_hours || null,
        actual_hours: task.actual_hours || null,
        budget: task.budget || null,
        tags: task.tags || '',
        is_billable: task.is_billable || false,
        task_notes: task.task_notes || ''
      };
      form.setFieldsValue(initialValues);
    } else {
      form.setFieldsValue({
        status: 'todo',
        priority: 'medium',
        progress_percentage: 0,
        is_billable: false
      });
    }
  }, [task, isEditing, form]);

  // ตรวจสอบความถูกต้องของข้อมูล
  const validateForm = (values) => {
    const validation = {};

    if (!values.task_title?.trim()) {
      validation.task_title = 'กรุณาระบุชื่องาน';
    }

    if (!values.task_description?.trim()) {
      validation.task_description = 'กรุณาระบุรายละเอียดงาน';
    }

    if (values.due_date && values.start_date) {
      if (values.due_date < values.start_date) {
        validation.due_date = 'วันที่กำหนดส่งต้องไม่เก่ากว่าวันที่เริ่มต้น';
      }
    }

    if (values.progress_percentage < 0 || values.progress_percentage > 100) {
      validation.progress_percentage = 'ความคืบหน้าต้องอยู่ระหว่าง 0-100';
    }

    return validation;
  };

  // บันทึกข้อมูล
  const handleSubmit = async (values) => {

    console.log('projectId:', projectId);


    const validation = validateForm(values);
    if (Object.keys(validation).length > 0) {
      form.setFields(Object.keys(validation).map(key => ({
        name: key,
        errors: [validation[key]]
      })));
      return;
    }

    try {
      setLoading(true);
      setErrors({});

      const submitData = {
        ...values,
        projectId: projectId,
        start_date: dayjs(values.start_date),
        due_date: dayjs(values.due_date),
        progress_percentage: values.progress_percentage,
        tags: values.tags ? values.tags.split(',').map(tag => tag.trim()).filter(tag => tag) : []
      };

      console.log('submitData', submitData);

      let response;
      if (isEditing) {
        response = await updateTask(task.id, submitData);
      } else {
        response = await createTask(projectId, submitData);
      }

      if (response.success) {
        onSave && onSave(response.data);
      } else {
        // จัดการ validation errors จาก server
        if (response.errors) {
          setValidationErrors(response.errors);
          form.setFields(Object.keys(response.errors).map(key => ({
            name: key,
            errors: [response.errors[key]]
          })));
        } else {
          setErrors({ general: response.message || 'เกิดข้อผิดพลาดในการบันทึกข้อมูล' });
        }
      }
    } catch (err) {
      console.error('Error saving task:', err);
      setErrors({ general: err.message || 'ไม่สามารถบันทึกข้อมูลได้' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={{
        status: 'todo',
        priority: 'medium',
        progress_percentage: 0,
        is_billable: false
      }}
    >
      {errors.general && (
        <Alert
          message="เกิดข้อผิดพลาด"
          description={errors.general}
          type="error"
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}

      <Row gutter={[24, 0]}>
        {/* ข้อมูลพื้นฐาน */}
        <Col span={24}>
          <Divider orientation="left">
            <Title level={4}>ข้อมูลพื้นฐาน</Title>
          </Divider>
        </Col>

        <Col span={24}>
          <Form.Item
            label="ชื่องาน"
            name="task_title"
            rules={[
              { required: true, message: 'กรุณาระบุชื่องาน' }
            ]}
          >
            <Input placeholder="ระบุชื่องาน" />
          </Form.Item>
        </Col>

        <Col span={24}>
          <Form.Item
            label="รายละเอียดงาน"
            name="task_description"
            rules={[
              { required: true, message: 'กรุณาระบุรายละเอียดงาน' }
            ]}
          >
            <TextArea
              rows={3}
              placeholder="ระบุรายละเอียดของงาน"
            />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12}>
          <Form.Item label="สถานะ" name="status">
            <Select>
              <Option value="todo">รอดำเนินการ</Option>
              <Option value="in_progress">กำลังดำเนินการ</Option>
              <Option value="testing">ทดสอบ</Option>
              <Option value="done">เสร็จสิ้น</Option>
            </Select>
          </Form.Item>
        </Col>

        <Col xs={24} sm={12}>
          <Form.Item label="ลำดับความสำคัญ" name="priority">
            <Select>
              <Option value="low">ต่ำ</Option>
              <Option value="medium">ปานกลาง</Option>
              <Option value="high">สูง</Option>
              <Option value="urgent">เร่งด่วน</Option>
            </Select>
          </Form.Item>
        </Col>

        <Col xs={24} sm={12}>
          <Form.Item label="ผู้รับผิดชอบ" name="assigned_to">
            <Select placeholder="เลือกผู้รับผิดชอบ" allowClear>
              {users.map(user => (
                <Option key={user.user_id} value={user.user_id}>
                  {user.full_name} ({user.username})
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        <Col xs={24} sm={12}>
          <Form.Item
            label="ความคืบหน้า (%)"
            name="progress_percentage"
            rules={[
              { type: 'number', min: 0, max: 100, message: 'ความคืบหน้าต้องอยู่ระหว่าง 0-100' }
            ]}
          >
            <InputNumber
              min={0}
              max={100}
              style={{ width: '100%' }}
              placeholder="0-100"
            />
          </Form.Item>
        </Col>

        {/* วันที่และเวลา */}
        <Col span={24}>
          <Divider orientation="left">
            <Title level={4}>วันที่และเวลา</Title>
          </Divider>
        </Col>

        <Col xs={24} sm={12}>
          <Form.Item label="วันที่เริ่มต้น" name="start_date">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12}>
          <Form.Item
            label="วันที่กำหนดส่ง"
            name="due_date"
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || !getFieldValue('start_date') || value >= getFieldValue('start_date')) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('วันที่กำหนดส่งต้องไม่เก่ากว่าวันที่เริ่มต้น'));
                },
              }),
            ]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12}>
          <Form.Item label="ชั่วโมงประมาณการ" name="estimated_hours">
            <InputNumber
              min={0}
              step={0.5}
              style={{ width: '100%' }}
              placeholder="จำนวนชั่วโมง"
            />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12}>
          <Form.Item label="ชั่วโมงจริง" name="actual_hours">
            <InputNumber
              min={0}
              step={0.5}
              style={{ width: '100%' }}
              placeholder="จำนวนชั่วโมงจริง"
            />
          </Form.Item>
        </Col>

        {/* การเงินและเพิ่มเติม */}
        <Col span={24}>
          <Divider orientation="left">
            <Title level={4}>การเงินและเพิ่มเติม</Title>
          </Divider>
        </Col>

        <Col xs={24} sm={12}>
          <Form.Item label="งบประมาณ" name="budget">
            <InputNumber
              min={0}
              step={0.01}
              style={{ width: '100%' }}
              placeholder="จำนวนเงิน"
              formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/\$\s?|(,*)/g, '')}
            />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12}>
          <Form.Item label="แท็ก" name="tags">
            <Input placeholder="แท็ก (คั่นด้วยเครื่องหมายจุลภาค)" />
          </Form.Item>
        </Col>

        <Col span={24}>
          <Form.Item name="is_billable" valuePropName="checked">
            <Checkbox>คิดค่าบริการ</Checkbox>
          </Form.Item>
        </Col>

        <Col span={24}>
          <Form.Item label="หมายเหตุ" name="task_notes">
            <TextArea
              rows={3}
              placeholder="หมายเหตุเพิ่มเติม (ไม่บังคับ)"
            />
          </Form.Item>
        </Col>
      </Row>

      {/* ปุ่มควบคุม */}
      <div style={{ marginTop: 24, textAlign: 'right' }}>
        <Space>
          <Button onClick={onCancel}>
            ยกเลิก
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            icon={isEditing ? <SaveOutlined /> : <PlusOutlined />}
          >
            {loading ? 'กำลังบันทึก...' : (isEditing ? 'อัปเดต' : 'สร้างงาน')}
          </Button>
        </Space>
      </div>
    </Form>
  )

}


export default TaskForm 